import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Calendar as CalendarIcon, MapPin, Plus, Send, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import EventCard from '@/components/EventCard';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = "/api";

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [eventDate, setEventDate] = useState(null);
  const [formData, setFormData] = useState({
    submitter_type: 'fan',
    submitter_name: '',
    submitter_email: '',
    event_title: '',
    venue_name: '',
    venue_address: '',
    description: '',
    time: '',
    performing_artists: '',
    headliner: '',
    ticket_price: '',
    ticket_url: '',
    is_free: false,
    is_all_ages: false,
    event_image: ''
  });

  useEffect(() => {
    fetchEvents();
  }, [selectedDate]);

  const fetchEvents = async () => {
    try {
      const params = selectedDate ? `?date=${format(selectedDate, 'yyyy-MM-dd')}` : '';
      const response = await axios.get(`${API}/events${params}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilter = () => {
    setSelectedDate(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitEvent = async (e) => {
    e.preventDefault();
    
    if (!formData.event_title || !formData.venue_name || !eventDate || !formData.time || !formData.submitter_name || !formData.submitter_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        date: format(eventDate, 'yyyy-MM-dd')
      };
      
      await axios.post(`${API}/events/submit`, payload);
      setSubmitted(true);
      toast.success('Event submitted for review!');
      
      // Reset form after 2 seconds
      setTimeout(() => {
        setFormData({
          submitter_type: 'fan',
          submitter_name: '',
          submitter_email: '',
          event_title: '',
          venue_name: '',
          venue_address: '',
          description: '',
          time: '',
          performing_artists: '',
          headliner: '',
          ticket_price: '',
          ticket_url: '',
          is_free: false,
          is_all_ages: false,
          event_image: ''
        });
        setEventDate(null);
        setSubmitted(false);
        setSubmitDialogOpen(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to submit event:', error);
      toast.error('Failed to submit event. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Group events by month
  const groupedEvents = events.reduce((acc, event) => {
    const date = new Date(event.date);
    const monthKey = format(date, 'MMMM yyyy');
    if (!acc[monthKey]) acc[monthKey] = [];
    acc[monthKey].push(event);
    return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="events-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-primary" />
          <span className="text-xs text-muted-foreground tracking-widest uppercase">Twin Cities Entertainment</span>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">Upcoming Shows</h1>
            <p className="text-muted-foreground max-w-2xl mt-2">
              Live music, concerts, and entertainment events happening in Minneapolis and St. Paul.
            </p>
          </div>
          
          {/* Submit Event Button */}
          <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2 bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90"
                data-testid="submit-event-btn"
              >
                <Plus size={18} />
                Submit an Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              {submitted ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-12 text-center"
                >
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold mb-2">Event Submitted!</h3>
                  <p className="text-muted-foreground">
                    Our team will review your submission within 24-48 hours.
                  </p>
                </motion.div>
              ) : (
                <>
                  <DialogHeader>
                    <DialogTitle className="text-2xl">Submit an Event</DialogTitle>
                    <DialogDescription>
                      Share a local event with the Twin Cities community. All submissions are reviewed before publishing.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <form onSubmit={handleSubmitEvent} className="space-y-6 mt-4">
                    {/* Event Details */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-primary">Event Details</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="event_title">Event Title *</Label>
                          <Input
                            id="event_title"
                            name="event_title"
                            placeholder="e.g., Live Jazz Night"
                            value={formData.event_title}
                            onChange={handleInputChange}
                            required
                            data-testid="event-title-input"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="headliner">Headliner / Main Artist</Label>
                          <Input
                            id="headliner"
                            name="headliner"
                            placeholder="e.g., The Local Band"
                            value={formData.headliner}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="performing_artists">All Performing Artists</Label>
                        <Input
                          id="performing_artists"
                          name="performing_artists"
                          placeholder="e.g., Band A, Band B, DJ Name (comma separated)"
                          value={formData.performing_artists}
                          onChange={handleInputChange}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          name="description"
                          placeholder="Tell us about the event..."
                          value={formData.description}
                          onChange={handleInputChange}
                          rows={3}
                          data-testid="event-description-input"
                        />
                      </div>
                    </div>
                    
                    {/* Venue & Time */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-primary">Venue & Time</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="venue_name">Venue Name *</Label>
                          <Input
                            id="venue_name"
                            name="venue_name"
                            placeholder="e.g., First Avenue"
                            value={formData.venue_name}
                            onChange={handleInputChange}
                            required
                            data-testid="event-venue-input"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="venue_address">Venue Address</Label>
                          <Input
                            id="venue_address"
                            name="venue_address"
                            placeholder="e.g., 701 1st Ave N, Minneapolis"
                            value={formData.venue_address}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Event Date *</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button 
                                variant="outline" 
                                className="w-full justify-start gap-2"
                                data-testid="event-date-trigger"
                              >
                                <CalendarIcon size={16} />
                                {eventDate ? format(eventDate, 'PPP') : 'Pick a date'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={eventDate}
                                onSelect={setEventDate}
                                initialFocus
                                disabled={(date) => date < new Date()}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="time">Start Time *</Label>
                          <Input
                            id="time"
                            name="time"
                            type="time"
                            value={formData.time}
                            onChange={handleInputChange}
                            required
                            data-testid="event-time-input"
                          />
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            name="is_free"
                            checked={formData.is_free}
                            onChange={handleInputChange}
                            className="w-4 h-4 rounded border-gray-600 bg-gray-800"
                          />
                          <span className="text-sm">Free Event</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            name="is_all_ages"
                            checked={formData.is_all_ages}
                            onChange={handleInputChange}
                            className="w-4 h-4 rounded border-gray-600 bg-gray-800"
                          />
                          <span className="text-sm">All Ages</span>
                        </label>
                      </div>
                    </div>
                    
                    {/* Tickets & Links */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-primary">Tickets & Media (Optional)</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="ticket_price">Ticket Price</Label>
                          <Input
                            id="ticket_price"
                            name="ticket_price"
                            placeholder="e.g., $15-$25"
                            value={formData.ticket_price}
                            onChange={handleInputChange}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="ticket_url">Ticket Link</Label>
                          <Input
                            id="ticket_url"
                            name="ticket_url"
                            type="url"
                            placeholder="https://..."
                            value={formData.ticket_url}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="event_image">Event Image URL</Label>
                        <Input
                          id="event_image"
                          name="event_image"
                          type="url"
                          placeholder="https://... (poster or promo image)"
                          value={formData.event_image}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    
                    {/* Submitter Info */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-primary">Your Information</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="submitter_name">Your Name *</Label>
                          <Input
                            id="submitter_name"
                            name="submitter_name"
                            placeholder="Your name"
                            value={formData.submitter_name}
                            onChange={handleInputChange}
                            required
                            data-testid="submitter-name-input"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="submitter_email">Your Email *</Label>
                          <Input
                            id="submitter_email"
                            name="submitter_email"
                            type="email"
                            placeholder="your@email.com"
                            value={formData.submitter_email}
                            onChange={handleInputChange}
                            required
                            data-testid="submitter-email-input"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="submitter_type">You Are *</Label>
                          <Select 
                            value={formData.submitter_type} 
                            onValueChange={(v) => handleSelectChange('submitter_type', v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select your role" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="band">Band / Artist</SelectItem>
                              <SelectItem value="venue">Venue</SelectItem>
                              <SelectItem value="promoter">Promoter</SelectItem>
                              <SelectItem value="fan">Fan / Attendee</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full gap-2"
                      disabled={submitting}
                      data-testid="submit-event-form-btn"
                    >
                      {submitting ? (
                        <>Submitting...</>
                      ) : (
                        <>
                          <Send size={16} />
                          Submit Event for Review
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </motion.div>

      {/* Event Registration CTA */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-xl border border-primary/30 bg-gradient-to-r from-primary/10 via-card to-purple-500/10 p-5 flex flex-col sm:flex-row items-center gap-4 sm:justify-between"
        data-testid="event-register-cta"
      >
        <div className="text-center sm:text-left">
          <p className="font-bold text-base">Got a show coming up?</p>
          <p className="text-sm text-muted-foreground">List your event on Twin Tube and reach the local scene.</p>
        </div>
        <Link
          to="/register/event"
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-sm transition-colors"
          data-testid="event-register-link"
        >
          Register Your Event <ArrowRight size={16} />
        </Link>
      </motion.div>

      {/* Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-wrap items-center gap-4"
      >
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="date-filter-trigger">
              <CalendarIcon size={16} />
              {selectedDate ? format(selectedDate, 'PPP') : 'Filter by date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              initialFocus
              data-testid="events-calendar"
            />
          </PopoverContent>
        </Popover>

        {selectedDate && (
          <Button variant="ghost" size="sm" onClick={clearFilter} data-testid="clear-filter-btn">
            Clear filter
          </Button>
        )}

        <span className="text-sm text-muted-foreground">
          {events.length} event{events.length !== 1 ? 's' : ''} found
        </span>
      </motion.div>

      {/* Events Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="aspect-[4/3]" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16"
        >
          <CalendarIcon size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="font-heading text-xl font-bold mb-2">No events found</h3>
          <p className="text-muted-foreground">
            {selectedDate ? 'Try selecting a different date' : 'Check back soon for upcoming shows'}
          </p>
        </motion.div>
      ) : (
        <div className="space-y-12">
          {Object.entries(groupedEvents).map(([month, monthEvents], index) => (
            <motion.section
              key={month}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
            >
              <h2 className="font-heading text-2xl font-bold mb-6 text-primary">{month}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {monthEvents.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            </motion.section>
          ))}
        </div>
      )}
    </div>
  );
};

export default Events;
